/* eslint-disable */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import MemberAvatar from "../components/MemberAvatar";
import SaveSpaceButton from "../components/SaveSpaceButton";

// Session 48 · Shared auth-aware header for the venue-workspace detail
// screens. Mirrors the /spaces/:slug PublicHeader so signed-in members see
// their avatar + sign-out instead of a misleading "Member sign in" link.
function DetailHeader() {
  const { user, logout } = useAuth() || {};
  const nav = useNavigate();
  return (
    <header className="rec-topbar">
      <Link to="/spaces" className="rec-brand" data-testid="workspace-brand">
        <span className="rec-brand-mark"><img src="/brand/metier-icon.png" alt="Métier" /></span>
      </Link>
      <nav className="rec-topnav">
        <Link to="/spaces">Spaces</Link>
        {user ? (
          <>
            <Link to="/profile" data-testid="workspace-user-chip" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <MemberAvatar user={user} size={26} clickable={false} />
              <span style={{ fontSize: 13 }}>{(user.name || user.email || "").split(" ")[0]}</span>
            </Link>
            <button
              type="button"
              onClick={async () => { await logout?.(); nav("/login"); }}
              data-testid="workspace-signout"
              style={{
                background: "transparent", border: "none",
                color: "inherit", cursor: "pointer",
                font: "inherit", padding: 0,
                letterSpacing: "0.14em", textTransform: "uppercase", fontSize: 11,
              }}
            >
              Sign out
            </button>
          </>
        ) : (
          <Link to="/login" data-testid="workspace-signin">Member sign in</Link>
        )}
      </nav>
    </header>
  );
}

const RESOURCE_TYPE_LABELS = {
  day_pass: "Day Pass", hot_desk: "Hot Desk", dedicated_desk: "Dedicated Desk",
  private_office: "Private Office", team_office: "Team Office", office_suite: "Office Suite",
  meeting_room: "Meeting Room", conference_room: "Conference Room", podcast_room: "Podcast Room",
  training_room: "Training Room", phone_booth: "Phone Booth", project_room: "Project Room",
  event_space: "Event Space", custom: "Workspace",
};

/**
 * VenueWorkspaceDetail — child workspace page under a parent venue.
 * URL shape: /spaces/:venueSlug/:workspaceSlug
 * Renders the workspace hero, capacity, amenities, description, and booking
 * CTA (direct external URL when the resource is external_link management,
 * otherwise a Métier-managed placeholder).
 */
export default function VenueWorkspaceDetail() {
  const { venueSlug, workspaceSlug } = useParams();
  const { user } = useAuth() || {};
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [sibs, setSibs] = useState([]);

  useEffect(() => {
    setData(null); setErr(""); setSibs([]);
    api.get(`/venues/${venueSlug}/resources/${workspaceSlug}`)
      .then(r => setData(r.data))
      .catch(() => setErr("Workspace not found."));
    api.get(`/venues/${venueSlug}/resources`)
      .then(r => setSibs((r.data.resources || []).filter(x => x.slug !== workspaceSlug).slice(0, 3)))
      .catch(() => {});
  }, [venueSlug, workspaceSlug]);

  if (err) return (
    <div className="rec-page">
      <DetailHeader />
      <div className="rec-main" style={{padding:"60px 24px"}}>
        <p className="m-form-err">{err}</p>
        <Link to={`/spaces/${venueSlug}`} className="m-btn-ghost" style={{marginTop:12,display:"inline-block"}}>← Back to venue</Link>
      </div>
    </div>
  );
  if (!data) return <div className="m-loading-inline" style={{padding:60}}>Loading workspace…</div>;

  const r = data.resource;
  const venue = data.location;
  const resTypeLabel = RESOURCE_TYPE_LABELS[r.workspace_type || r.venue_type] || "Workspace";
  const bookingUrl = r.external_booking_url;
  const isExternal = r.management_mode === "external_link" && bookingUrl;
  // Attach UTM params for external bookings so we can attribute revenue back.
  const utmUrl = isExternal ? `${bookingUrl}${bookingUrl.includes("?") ? "&" : "?"}utm_source=metier&utm_medium=referral&utm_campaign=workspace_booking&utm_content=${encodeURIComponent(r.slug)}` : null;

  return (
    <div className="rec-page" data-testid="venue-workspace-detail">
      <DetailHeader />

      <main className="rec-main" style={{paddingBottom:80}}>
        <nav className="venue-breadcrumb" aria-label="Breadcrumb" data-testid="workspace-breadcrumb" style={{padding:"14px 0 6px",fontSize:13,color:"var(--m-muted)"}}>
          <Link to="/spaces" style={{color:"var(--m-muted)"}}>Spaces</Link>
          <span style={{margin:"0 8px"}}>·</span>
          <Link to={`/spaces/${venueSlug}`} style={{color:"var(--gold)"}}>{venue.name}</Link>
          <span style={{margin:"0 8px"}}>·</span>
          <span style={{color:"var(--navy)"}}>{r.name}</span>
        </nav>

        <div className="rec-hero">
          {(() => {
            // Session 53.7 · Cascade through images → photos → gallery so
            // admin uploads (photos[]), Rec-Mode gallery entries, and legacy
            // images all render the hero. Falls back to the parent venue's
            // photos if the resource has none.
            const src =
              (r.images && r.images[0])
              || (r.photos && r.photos[0])
              || (r.gallery && r.gallery[0])
              || (venue.images && venue.images[0])
              || (venue.photos && venue.photos[0])
              || (venue.gallery && venue.gallery[0])
              || null;
            return src ? <img src={src} alt={r.name} className="rec-hero-img" /> : null;
          })()}
        </div>

        <div className="rec-detail-grid">
          <section className="rec-detail-main">
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div className="m-eyebrow" style={{color:"var(--gold)"}}>{resTypeLabel}{r.capacity > 0 && <> · Up to {r.capacity} {r.capacity === 1 ? "person" : "people"}</>}</div>
              {user && (
                <SaveSpaceButton
                  spaceType="workspace"
                  spaceId={r.slug || r.workspace_id || r.resource_id}
                  spaceName={r.name}
                  variant="pill"
                />
              )}
              {/* Session 47 · Admin/owner shortcut into the edit UI. */}
              {user?.role === "admin" && (
                <Link
                  to={r.workspace_id
                    ? `/admin/workspaces/${r.workspace_id}/edit`
                    : `/admin/venues/${venue.location_id || venueSlug}/edit`}
                  data-testid="workspace-manage-listing"
                  style={{
                    marginLeft: "auto",
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "5px 12px", fontSize: 11,
                    letterSpacing: "0.14em", textTransform: "uppercase",
                    fontWeight: 600,
                    border: "1px solid var(--gold, #B8913A)",
                    borderRadius: 999,
                    color: "var(--gold, #B8913A)",
                    textDecoration: "none",
                    background: "rgba(184,145,58,0.06)",
                  }}
                  title="Open in Admin editor"
                >
                  <span aria-hidden="true">⚙</span> Manage listing
                </Link>
              )}
            </div>
            <h1 className="rec-h1">{r.name}</h1>
            <p className="m-muted" style={{marginTop:8}}>at <Link to={`/spaces/${venueSlug}`} style={{color:"var(--gold)"}}>{venue.name}</Link> · {venue.address}</p>

            {r.description && (
              <>
                <h3 className="rec-h3">About this workspace</h3>
                <p className="rec-desc">{r.description}</p>
              </>
            )}

            {r.amenities && r.amenities.length > 0 && (
              <>
                <h3 className="rec-h3">Amenities</h3>
                <ul className="rec-amenities" data-testid="workspace-amenities">
                  {r.amenities.map(a => <li key={a}><span className="rec-am-dot" />{a.replace(/_/g, " ")}</li>)}
                </ul>
              </>
            )}

            {sibs.length > 0 && (
              <>
                <h3 className="rec-h3">Also at {venue.name}</h3>
                <div className="venue-children-grid" style={{marginTop:12}}>
                  {sibs.map(sr => (
                    <Link key={sr.resource_id} to={`/spaces/${venueSlug}/${sr.slug}`} className="venue-child-card" data-testid={`sibling-${sr.slug}`} style={{textDecoration:"none",color:"inherit"}}>
                      <div className="venue-child-body">
                        <div className="venue-child-eyebrow">{RESOURCE_TYPE_LABELS[sr.workspace_type || sr.venue_type] || "Workspace"}</div>
                        <h4 className="venue-child-name">{sr.name}</h4>
                        {sr.starting_price_cents > 0 && <div className="venue-child-price">From ${(sr.starting_price_cents/100).toFixed(0)}{sr.starting_price_unit === "hour" ? "/hour" : sr.starting_price_unit === "day" ? "/day" : ""}</div>}
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </section>

          <aside className="rec-widget" id="booking">
            <div className="rec-widget-card">
              {(() => {
                // Session 43 · Support both venue-type (r.venue.pricing) and
                // workspace-type (r.workspace.pricing) resources. Previously
                // venues (like Rec-Mode Event Space) showed "Contact venue"
                // because we only inspected r.workspace.
                const wsHourly = r.workspace?.pricing?.hourly_cents || 0;
                const wsDay = r.workspace?.pricing?.day_pass_cents || 0;
                const vBase = r.venue?.pricing?.base_rate_cents || 0;
                const vModel = r.venue?.pricing?.model || "";
                const priceCents = wsHourly || wsDay || vBase;
                const priceUnit = wsHourly ? "/hour"
                  : wsDay ? "/day"
                  : vModel === "hybrid" ? "/hour"
                  : vModel === "flat" ? "/event"
                  : vModel === "day" ? "/day"
                  : "";
                if (priceCents > 0) {
                  return (
                    <div className="rec-widget-price">
                      <em>${(priceCents / 100).toFixed(0)}</em>
                      <span>{priceUnit}</span>
                    </div>
                  );
                }
                return (
                  <div className="m-muted" style={{fontSize:14,marginBottom:12}}>
                    Contact venue for current pricing.
                  </div>
                );
              })()}
              {r.venue?.minimum_hours > 0 && (
                <div style={{fontSize:12, color:"var(--m-muted)", marginBottom:12}}
                     data-testid="workspace-min-hours">
                  {r.venue.minimum_hours}-hour minimum
                </div>
              )}
              {isExternal && utmUrl ? (
                <a
                  href={utmUrl}
                  target="_blank"
                  rel="noopener"
                  className="m-btn-gold w-full"
                  style={{display:"block",textAlign:"center",textDecoration:"none"}}
                  data-testid="workspace-book-external"
                >
                  {r.booking_cta_override || "Book direct →"}
                </a>
              ) : (
                // Session 43 · Route Métier-managed bookings into the
                // deposit + balance request-to-book flow (v2 checkout).
                // For request_to_book, this goes through the deposit
                // workflow at /spaces/:venue/:workspace with the modal
                // opened; for now we deep-link to a mailto with the venue
                // owner as an interim capture path until the modal is
                // wired for parent-venue resources.
                <a
                  href={`mailto:${(r.venue?.contact_email || (data.location?.owner_emails || [])[0] || "contact@workmetier.com")}?subject=${encodeURIComponent(`Booking request: ${r.name}`)}&body=${encodeURIComponent(`Hello — I'd like to book ${r.name} at ${venue.name}.\n\nPreferred date:\nStart time:\nDuration:\n\nName:\nEmail:\nPhone:\n\nThanks!`)}`}
                  className="m-btn-gold w-full"
                  style={{display:"block",textAlign:"center",textDecoration:"none"}}
                  data-testid="workspace-book-metier"
                >
                  {r.cta_text || "Request availability →"}
                </a>
              )}
              {r.cta_disclosure && (
                <p className="m-muted" style={{fontSize:12,marginTop:10,lineHeight:1.6}}
                   data-testid="workspace-cta-disclosure">
                  {r.cta_disclosure}
                </p>
              )}
              <p className="m-muted" style={{fontSize:12,marginTop:12,lineHeight:1.6}}>
                Booking through {venue.name}. Métier is a boutique membership network — verify current
                availability with the venue directly.
              </p>
            </div>
          </aside>
        </div>
      </main>

      <footer className="rec-footer">
        <span>© Métier · workmetier.com</span>
        <span><Link to={`/spaces/${venueSlug}`} style={{color:"var(--gold)"}}>← Back to {venue.name}</Link></span>
      </footer>
    </div>
  );
}
